import { useCallback, useState } from 'react';
import type {
  ScaffolderTask,
  ScaffolderTaskStatus,
} from '@backstage/plugin-scaffolder-common';

const STORAGE_KEY = 'backstage:task-explorer:saved-tasks';

type SavedTask = Pick<ScaffolderTask, 'id' | 'createdAt'> & {
  createdBy: string;
  status: ScaffolderTaskStatus;
  templateRef: string;
  templateTitle: string;
};

type UseSavedTasksResult = {
  savedTasks: SavedTask[];
  isSaved: (taskId: string) => boolean;
  saveTask: (task: SavedTask) => void;
  removeTask: (taskId: string) => void;
  clearAll: () => void;
};

const loadSavedTasks = (): SavedTask[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as SavedTask[];
  } catch {
    return [];
  }
};

const persistSavedTasks = (tasks: SavedTask[]): void => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(tasks));
};

export type { SavedTask };

export const useSavedTasks = (): UseSavedTasksResult => {
  const [savedTasks, setSavedTasks] = useState<SavedTask[]>(loadSavedTasks);

  const isSaved = useCallback(
    (taskId: string) => savedTasks.some(saved => saved.id === taskId),
    [savedTasks],
  );

  const saveTask = useCallback((task: SavedTask) => {
    setSavedTasks(prev => {
      if (prev.some(existing => existing.id === task.id)) return prev;
      const updated = [task, ...prev];
      persistSavedTasks(updated);
      return updated;
    });
  }, []);

  const removeTask = useCallback((taskId: string) => {
    setSavedTasks(prev => {
      const updated = prev.filter(saved => saved.id !== taskId);
      persistSavedTasks(updated);
      return updated;
    });
  }, []);

  const clearAll = useCallback(() => {
    setSavedTasks([]);
    persistSavedTasks([]);
  }, []);

  return { savedTasks, isSaved, saveTask, removeTask, clearAll };
};
